// ============================================
// IMAGE COMPRESSION SERVICE
// ============================================
// Automatically resizes and compresses photos on upload
// - Preserves original aspect ratio exactly (no distortion)
// - Scales longest edge to max 1280px
// - Compresses to JPEG, targeting under 200KB

import {
    buildCompressedPhotoCloudMedia,
    buildLocalOriginalCloudMedia,
} from './cloud-media-policy';

function compressedPhotoCloudMedia({ data, sizeKB, width, height }) {
    const contentType = /^data:(image\/(?:jpeg|webp));base64,/i.exec(String(data || ''))?.[1]?.toLowerCase()
        || ImageCompressionService.FORMAT;
    return buildCompressedPhotoCloudMedia({
        derivativeBytes: Math.max(1, Math.round(Number(sizeKB) * 1024)),
        contentType,
        width: Number(width),
        height: Number(height),
        processor: 'browser-canvas',
    });
}

// Yield control to the browser's main thread to prevent freezing during heavy loops
export const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));

// Detect WebP support once at module load
const WEBP_SUPPORTED = (() => {
    try {
        const c = document.createElement('canvas');
        c.width = c.height = 1;
        return c.toDataURL('image/webp').startsWith('data:image/webp');
    } catch { return false; }
})();

export const ImageCompressionService = {
    // Settings
    MAX_EDGE: 1280,           // Max pixels for longest edge (good quality for markup + print)
    TARGET_SIZE_KB: 200,      // Target file size in KB
    INITIAL_QUALITY: 0.85,    // Start compression at 85% quality
    MIN_QUALITY: 0.3,         // Allow lower quality to hit 200KB target
    FORMAT: WEBP_SUPPORTED ? 'image/webp' : 'image/jpeg',
    
    // Generate unique photo code like "A1B2" from filename and timestamp
    generatePhotoCode(originalName, index) {
        // Extract meaningful part of filename (without extension)
        const baseName = originalName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9]/g, '');
        
        // Take first letter (uppercase) + first number found (or index) + random chars
        const firstLetter = (baseName.match(/[a-zA-Z]/) || ['P'])[0].toUpperCase();
        const firstNumber = (baseName.match(/\d/) || [index])[0];
        const randomChars = Math.random().toString(36).substring(2, 4).toUpperCase();
        
        return `${firstLetter}${firstNumber}${randomChars}`;
    },
    
    // Generate formatted filename: Photo_001_A1B2.jpg
    generateFileName(index, originalName) {
        const paddedIndex = String(index).padStart(3, '0');
        const photoCode = this.generatePhotoCode(originalName, index);
        return `Photo_${paddedIndex}_${photoCode}.jpg`;
    },

    // Whether anything was drawn on the canvas. A decoded photo leaves opaque
    // pixels; a photo the browser could not decode leaves the canvas fully
    // transparent. A 16 x 16 sample is enough to tell. Where the canvas cannot
    // be read back (a test double), the photo is taken as drawn.
    canvasHasPixels(canvas) {
        try {
            const probe = document.createElement('canvas');
            probe.width = 16;
            probe.height = 16;
            const probeCtx = probe.getContext('2d');
            if (!probeCtx || typeof probeCtx.getImageData !== 'function') return true;
            probeCtx.drawImage(canvas, 0, 0, 16, 16);
            const { data } = probeCtx.getImageData(0, 0, 16, 16);
            if (!data || !data.length) return true;
            for (let i = 3; i < data.length; i += 4) {
                if (data[i] !== 0) return true;
            }
            return false;
        } catch {
            return true;
        }
    },

    // Load image from file or data URL
    async loadImage(source) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load image'));
            
            if (source instanceof File) {
                const reader = new FileReader();
                reader.onload = (e) => { img.src = e.target.result; };
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsDataURL(source);
            } else {
                img.src = source;
            }
        });
    },
    
    // Calculate new dimensions - PRESERVES ORIGINAL ASPECT RATIO
    // Scales so the longest edge is at most MAX_EDGE pixels
    // Examples:
    //   4000x3000 (landscape) → 1280x960  (ratio 4:3 preserved)
    //   3000x4000 (portrait)  → 960x1280  (ratio 3:4 preserved)
    //   4000x4000 (square)    → 1280x1280 (ratio 1:1 preserved)
    //   800x600 (small)       → 800x600   (no change, already under max)
    calculateDimensions(width, height) {
        const longestEdge = Math.max(width, height);
        
        // Only scale down if image exceeds max edge
        if (longestEdge <= this.MAX_EDGE) {
            return { width, height };
        }
        
        // Calculate scale factor to fit longest edge within MAX_EDGE
        const scale = this.MAX_EDGE / longestEdge;
        
        return {
            width: Math.round(width * scale),
            height: Math.round(height * scale)
        };
    },
    
    // Encode a canvas to a data URL WITHOUT blocking the main thread.
    //
    // `canvas.toDataURL()` is SYNCHRONOUS — encoding a 960×1280 photo blocks the
    // single UI thread for tens of ms, and the iterative quality loop below does
    // it several times per photo × dozens of photos = a frozen tab on import.
    // `canvas.toBlob()` runs the same encode asynchronously (off the main thread
    // in modern browsers) and awaiting it yields the event loop. Returns
    // { dataUrl, sizeKB } using the EXACT byte size (blob.size) so the quality
    // search needs no base64 math. Falls back to sync toDataURL where toBlob is
    // unavailable (older browsers / test env).
    async encodeCanvas(canvas, quality) {
        const type = ImageCompressionService.FORMAT;
        if (canvas && typeof canvas.toBlob === 'function') {
            const blob = await new Promise((resolve) => {
                try { canvas.toBlob(resolve, type, quality); } catch { resolve(null); }
            });
            if (blob) {
                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || ''));
                    reader.onerror = () => reject(reader.error || new Error('FileReader failed reading canvas blob'));
                    reader.readAsDataURL(blob);
                });
                return { dataUrl, sizeKB: blob.size / 1024 };
            }
        }
        // Fallback: synchronous encode.
        const dataUrl = canvas.toDataURL(type, quality);
        return { dataUrl, sizeKB: this.getDataUrlSizeKB(dataUrl) };
    },

    // Compress image with canvas (kept for external callers — non-blocking encode).
    async compressImage(img, quality) {
        const { width, height } = this.calculateDimensions(img.width, img.height);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        const { dataUrl } = await this.encodeCanvas(canvas, quality);
        canvas.width = 0; canvas.height = 0; // free memory
        return dataUrl;
    },
    
    // Get size of data URL in KB
    getDataUrlSizeKB(dataUrl) {
        // Remove header (data:image/jpeg;base64,)
        const base64 = dataUrl.split(',')[1] || dataUrl;
        // Base64 to bytes: 4 base64 chars = 3 bytes
        const bytes = (base64.length * 3) / 4;
        // Account for padding
        const padding = (base64.match(/=+$/) || [''])[0].length;
        return (bytes - padding) / 1024;
    },
    
    // Main compression function - iteratively compresses until under target size
    async compress(source, originalName = 'photo.jpg', index = 1) {
        try {
            console.log(` Compressing: ${originalName}`);
            
            // Load the image
            const img = await this.loadImage(source);
            const originalWidth = img.width;
            const originalHeight = img.height;
            const { width, height } = this.calculateDimensions(originalWidth, originalHeight);
            
            // Log dimensions - show if aspect ratio is preserved
            const originalRatio = (originalWidth / originalHeight).toFixed(2);
            const newRatio = (width / height).toFixed(2);
            console.log(`   Original: ${originalWidth}x${originalHeight} (ratio ${originalRatio})`);
            console.log(`   Target:   ${width}x${height} (ratio ${newRatio}) - aspect ${originalRatio === newRatio ? '✓ preserved' : 'changed'}`);
            
            // Draw the (downscaled) image to a canvas ONCE. Re-encoding the same
            // canvas at different qualities is cheap; the old code redrew + made a
            // fresh canvas on every quality step (wasted CPU × every photo).
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            // A photo the browser cannot decode (a file that ends early, like
            // the Bellevue photo slice B0 found) still loads and reports its
            // size from its header, then draws nothing. Encoding that canvas
            // stored an empty picture as the photo, with no sign of a problem
            // (slice EVID, 2026-10-25). It is refused here, by name, instead.
            if (!this.canvasHasPixels(canvas)) {
                canvas.width = 0; canvas.height = 0;
                const unreadable = new Error(`"${originalName}" could not be read: the file looks damaged (it may end early). Export it again from the phone or camera, then add it again.`);
                unreadable.code = 'PHOTO_UNREADABLE';
                throw unreadable;
            }

            // Quality search: start high, then take BIG (0.2) steps so we converge
            // in ≤3 extra encodes instead of up to 9. Every encode is non-blocking
            // (toBlob), so the UI stays responsive even on photo-heavy folders.
            let quality = this.INITIAL_QUALITY;
            let { dataUrl: compressed, sizeKB } = await this.encodeCanvas(canvas, quality);
            console.log(`   Initial compression: ${sizeKB.toFixed(1)}KB at quality ${(quality * 100).toFixed(0)}%`);

            let attempts = 0;
            while (sizeKB > this.TARGET_SIZE_KB && quality > this.MIN_QUALITY && attempts < 4) {
                quality = Math.max(this.MIN_QUALITY, Math.round((quality - 0.2) * 100) / 100);
                ({ dataUrl: compressed, sizeKB } = await this.encodeCanvas(canvas, quality));
                attempts++;
                console.log(`   Reducing quality: ${sizeKB.toFixed(1)}KB at ${(quality * 100).toFixed(0)}%`);
            }

            canvas.width = 0; canvas.height = 0; // free canvas memory

            const newFileName = this.generateFileName(index, originalName);

            console.log(` Done: ${newFileName} | ${originalWidth}x${originalHeight} → ${width}x${height} | ${sizeKB.toFixed(0)}KB`);

            return {
                data: compressed,
                fileName: newFileName,
                originalName: originalName,
                width: width,
                height: height,
                sizeKB: sizeKB,
                quality: quality,
                cloudMedia: compressedPhotoCloudMedia({ data: compressed, sizeKB, width, height }),
            };
        } catch (err) {
            console.error(` Compression failed for ${originalName}:`, err);
            throw err;
        }
    },
    
    // Batch compress multiple images
    async compressBatch(files, startIndex = 1, onProgress = null) {
        const results = [];
        const errors = [];
        
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const index = startIndex + i;
            
            if (onProgress) {
                onProgress({
                    current: i + 1,
                    total: files.length,
                    fileName: file.name,
                    status: 'processing'
                });
            }
            
            try {
                const result = await this.compress(file, file.name, index);
                results.push(result);
            } catch (err) {
                console.error(`Failed to compress ${file.name}:`, err);
                errors.push({ name: file.name, error: err.message });
                
                // Fallback: try to use original if compression fails
                try {
                    const reader = new FileReader();
                    const fallbackData = await new Promise((resolve, reject) => {
                        reader.onload = (e) => resolve(e.target.result);
                        reader.onerror = () => reject(new Error('Failed to read'));
                        reader.readAsDataURL(file);
                    });
                    
                    results.push({
                        data: fallbackData,
                        fileName: this.generateFileName(index, file.name),
                        originalName: file.name,
                        width: null,
                        height: null,
                        sizeKB: file.size / 1024,
                        quality: 1.0,
                        fallback: true,
                        cloudMedia: buildLocalOriginalCloudMedia('photo_compression_failed'),
                    });
                    console.log(`Using fallback (original) for ${file.name}`);
                } catch {
                    // Complete failure - skip this file
                    console.error(` Complete failure for ${file.name}, skipping`);
                }
            }
        }
        
        if (onProgress) {
            onProgress({
                current: files.length,
                total: files.length,
                status: 'complete',
                results: results.length,
                errors: errors.length
            });
        }
        
        return { results, errors };
    }
};

// ============================================
// HERO IMAGE COMPRESSION SERVICE
// ============================================
// Compresses hero images to reduce PDF file size
// - Targets 1280px max width (hero is typically landscape)
// - Compresses to ~150KB for optimal PDF size
export const HeroImageCompressor = {
    MAX_WIDTH: 1280,          // Hero images are typically wide landscape
    MAX_HEIGHT: 800,          // Limit height too
    TARGET_SIZE_KB: 150,      // Target smaller for hero images
    INITIAL_QUALITY: 0.85,
    MIN_QUALITY: 0.5,
    
    // Load image from data URL or File
    async loadImage(source) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            
            if (typeof source === 'string') {
                img.src = source;
            } else if (source instanceof File) {
                const reader = new FileReader();
                reader.onload = (e) => { img.src = e.target.result; };
                reader.onerror = reject;
                reader.readAsDataURL(source);
            }
        });
    },
    
    // Calculate dimensions maintaining aspect ratio
    calculateDimensions(width, height) {
        let scale = 1;
        
        if (width > this.MAX_WIDTH) {
            scale = this.MAX_WIDTH / width;
        }
        if (height * scale > this.MAX_HEIGHT) {
            scale = this.MAX_HEIGHT / height;
        }
        
        return {
            width: Math.round(width * scale),
            height: Math.round(height * scale)
        };
    },
    
    // Compress image with canvas
    async compressToDataUrl(img, quality) {
        const { width, height } = this.calculateDimensions(img.width, img.height);
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        return canvas.toDataURL('image/jpeg', quality);
    },
    
    // Get size of data URL in KB
    getDataUrlSizeKB(dataUrl) {
        if (!dataUrl || !dataUrl.includes(',')) return 0;
        const base64 = dataUrl.split(',')[1];
        if (!base64) return 0;
        const bytes = (base64.length * 3) / 4;
        const padding = (base64.match(/=+$/) || [''])[0].length;
        return (bytes - padding) / 1024;
    },
    
    // Main compression function
    async compress(source) {
        try {
            // Skip if already small or not a data URL
            if (typeof source === 'string') {
                const currentSize = this.getDataUrlSizeKB(source);
                if (currentSize < this.TARGET_SIZE_KB && currentSize > 0) {
                    console.log(` Hero image already small (${currentSize.toFixed(0)}KB), skipping compression`);
                    return source;
                }
            }
            
            console.log(' Compressing hero image...');
            const img = await this.loadImage(source);
            const originalSize = typeof source === 'string' ? this.getDataUrlSizeKB(source) : 'unknown';
            console.log(`   Original: ${img.width}x${img.height}, ${originalSize}KB`);
            
            // Start compression
            let quality = this.INITIAL_QUALITY;
            let compressed = await this.compressToDataUrl(img, quality);
            let sizeKB = this.getDataUrlSizeKB(compressed);
            
            // Iteratively reduce quality if needed
            let attempts = 0;
            while (sizeKB > this.TARGET_SIZE_KB && quality > this.MIN_QUALITY && attempts < 8) {
                quality -= 0.1;
                compressed = await this.compressToDataUrl(img, quality);
                sizeKB = this.getDataUrlSizeKB(compressed);
                attempts++;
                if (attempts % 2 === 0) await yieldToMain();
            }
            
            const { width, height } = this.calculateDimensions(img.width, img.height);
            console.log(` Hero compressed: ${width}x${height}, ${sizeKB.toFixed(0)}KB (quality ${(quality * 100).toFixed(0)}%)`);
            
            return compressed;
        } catch (err) {
            console.error(' Hero image compression failed:', err);
            // Return original if compression fails
            return typeof source === 'string' ? source : null;
        }
    }
};

/**
 * Compress an image that arrived OUTSIDE the Stage 1 import loop.
 *
 * Stage 1 compresses every photo it ingests. Several other paths did not, and
 * the difference was invisible until it wasn't: on 2026-08-18 uncompressed
 * photos put 3.6-7.9 MB each into `photos[]`, six of them blew the 30 MB vision
 * cap, and the oversized save envelopes helped push the backend out of heap.
 *
 * Returns the SAME shape whether or not compression worked, and says which. A
 * failure is not fatal - a full-size photo is better than a lost one - but it
 * is logged loudly, because "quietly full size" is the state that caused the
 * outage.
 */
export async function compressIngestedImage(dataUrl, name = 'photo.jpg', index = 1) {
    try {
        const c = await ImageCompressionService.compress(dataUrl, name, index);
        if (!c?.data) throw new Error('compressor returned no data');
        return {
            data: c.data,
            sizeKB: c.sizeKB,
            width: c.width,
            height: c.height,
            fileName: c.fileName,
            compressed: true,
            cloudMedia: c.cloudMedia || compressedPhotoCloudMedia(c),
        };
    } catch (err) {
        console.warn(
            `[image] Compression failed for "${name}" — keeping the original at FULL SIZE. `
            + 'Large photos are what exceeded the vision-request cap on 2026-08-18:',
            err?.message || err,
        );
        // A damaged photo (EVID) stays a local original like any failed one,
        // never uploaded, with a reason that says it could not be read.
        return {
            data: dataUrl,
            compressed: false,
            cloudMedia: buildLocalOriginalCloudMedia(
                err?.code === 'PHOTO_UNREADABLE' ? 'photo_unreadable' : 'photo_compression_failed',
            ),
        };
    }
}
